import { useState, useEffect, useCallback, useRef } from 'react';
import { TargetDetails } from '@/types/workspace';
import { INITIAL_TARGET_DETAILS } from '@/constants/app';
import { api } from '@/api/client';
import { getStorageData, setStorageData } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { handleAppError } from '@/lib/errorUtils';
import { useMutation } from '@tanstack/react-query';

interface WorkspaceData {
  jobText: string;
  jobLink: string;
  targetDetails: TargetDetails;
}

export function useWorkspace(id: string, recordUsage: (cost: number, promptToken: number, completionToken: number) => void) {
  const { user } = useAuth();
  const [data, setData] = useState<WorkspaceData>({
    jobText: '',
    jobLink: '',
    targetDetails: INITIAL_TARGET_DETAILS,
  });
  const [isFetchingLink, setIsFetchingLink] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState<string | undefined>(undefined);
  
  // Use a ref to always access the latest user ID when saving
  const currentUserId = useRef(user?.uid);
  useEffect(() => {
    currentUserId.current = user?.uid;
  }, [user?.uid]);

  const updateData = useCallback((updates: Partial<WorkspaceData> | ((prev: WorkspaceData) => WorkspaceData)) => {
    setData((prev) => {
      const next = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      // Save synchronously to local storage representation
      if (isLoaded) {
        setStorageData(`workspace_${id}`, next, currentUserId.current).catch(err => {
          handleAppError(err, 'Failed to save workspace');
        });
      }
      return next;
    });
  }, [id, isLoaded]);

  const setJobText = useCallback((val: string | ((prev: string) => string)) => {
    updateData(prev => ({ ...prev, jobText: typeof val === 'function' ? val(prev.jobText) : val }));
  }, [updateData]);

  const setJobLink = useCallback((val: string | ((prev: string) => string)) => {
    updateData(prev => ({ ...prev, jobLink: typeof val === 'function' ? val(prev.jobLink) : val }));
  }, [updateData]);

  const setTargetDetails = useCallback((val: TargetDetails | ((prev: TargetDetails) => TargetDetails)) => {
    updateData(prev => ({ ...prev, targetDetails: typeof val === 'function' ? val(prev.targetDetails) : val }));
  }, [updateData]);

  useEffect(() => {
    let active = true;
    const loadState = async () => {
      if (active) setIsLoaded(false);
      try {
        const saved = await getStorageData<WorkspaceData>(`workspace_${id}`, user?.uid);
        
        if (active) {
          setData({
            jobText: saved?.jobText || '',
            jobLink: saved?.jobLink || '',
            targetDetails: saved?.targetDetails || INITIAL_TARGET_DETAILS
          });
        }
      } catch (e) {} finally {
        if (active) {
          setIsLoaded(true);
          setLoadedUserId(user?.uid);
        }
      }
    };
    loadState();
    return () => { active = false; };
  }, [id, user?.uid]); // Reload if user changes

  const extractMutation = useMutation({
    mutationFn: async (textToExtract: string) => {
      return await api.extractJobMetadata(textToExtract);
    },
    onSuccess: ({ data: metadata, usage }) => {
      if (usage) {
         recordUsage(usage.cost, usage.promptTokens, usage.completionTokens);
      }
      
      if (metadata) {
        const normalizeBoolUnknown = (val: any) => {
          if (!val) return 'Unknown';
          const str = String(val).toLowerCase();
          if (str === 'yes' || str === 'true') return 'Yes';
          if (str === 'no' || str === 'false') return 'No';
          return 'Unknown';
        };

        const normalizeJobType = (val: any) => {
          if (!val) return '';
          const str = String(val).toLowerCase();
          if (str.includes('full')) return 'full-time';
          if (str.includes('part')) return 'part-time';
          if (str.includes('contract')) return 'contract';
          if (str.includes('intern')) return 'internship';
          if (str.includes('free')) return 'freelance';
          return val;
        };

        setTargetDetails(prev => ({
          ...prev,
          company: metadata.company || prev.company,
          position: metadata.position || prev.position,
          targetLocation: metadata.targetLocation !== undefined ? metadata.targetLocation : prev.targetLocation,
          jobType: normalizeJobType(metadata.jobType) || prev.jobType,
          workModel: metadata.workModel || prev.workModel,
          visaSponsorship: normalizeBoolUnknown(metadata.visaSponsorship) as any,
          visaSponsorshipReference: metadata.visaSponsorshipReference || prev.visaSponsorshipReference,
          visaSponsorshipLink: metadata.visaSponsorshipLink || prev.visaSponsorshipLink,
          relocation: normalizeBoolUnknown(metadata.relocation) as any,
          relocationReference: metadata.relocationReference || prev.relocationReference,
          relocationLink: metadata.relocationLink || prev.relocationLink,
          salaryRange: metadata.salaryRange || prev.salaryRange,
          salaryRangeReference: metadata.salaryRangeReference || prev.salaryRangeReference,
          salaryRangeLink: metadata.salaryRangeLink || prev.salaryRangeLink,
        }));
      }
    },
    onError: (err) => {}
  });

  const extractMetadataForText = async (textToExtract: string) => {
    if (!textToExtract || textToExtract.trim().length < 50) return;
    return extractMutation.mutateAsync(textToExtract);
  };

  const resetTargetParams = () => {
    updateData({
      jobText: '',
      jobLink: '',
      targetDetails: INITIAL_TARGET_DETAILS
    });
  };

  return {
    jobText: data.jobText, setJobText,
    jobLink: data.jobLink, setJobLink,
    targetDetails: data.targetDetails, setTargetDetails,
    isFetchingLink, setIsFetchingLink,
    isExtracting: extractMutation.isPending, 
    extractMetadataForText,
    resetTargetParams,
    isLoaded
  };
}
